import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, MapPin } from 'lucide-react';
import { assignmentsService, Assignment } from '../../services/assignmentsService';
import { useToast } from '../../context/ToastContext';
import { PageHeader } from '../../components/shared/PageHeader';
import { Pagination } from '../../components/shared/Pagination';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

const OverduePage: React.FC = () => {
  const { showToast } = useToast();
  const [items, setItems] = useState<Assignment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(25);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const result = await assignmentsService.getOverdue({ page, limit: pageSize });
      setItems(result.data);
      setTotal(result.total);
    } catch {
      showToast('Failed to load overdue items', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetch(); }, [fetch]);
  const totalPages = Math.ceil(total / pageSize);

  const daysOverdue = (dueDate: string) => {
    const diff = Date.now() - new Date(dueDate).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Overdue Items" description={`${total} items past their due date`}>
        <AlertTriangle className="w-6 h-6 text-destructive" />
      </PageHeader>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Asset Tag</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Qty Out</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead className="text-right text-destructive">Days Overdue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-success font-medium">No overdue items!</TableCell></TableRow>
              ) : items.map((a) => (
                <TableRow key={a.id} className="hover:bg-destructive/10/50">
                  <TableCell>{a.assignee?.firstName} {a.assignee?.lastName}</TableCell>
                  <TableCell>{a.catalogItem?.name}</TableCell>
                  <TableCell className="font-mono">{a.assetUnit?.assetTag || '—'}</TableCell>
                  <TableCell>
                    {a.issuedFromLocation?.name ? (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                        {a.issuedFromLocation.name}
                      </div>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{a.department?.name || '—'}</TableCell>
                  <TableCell className="text-right font-bold">{a.quantity - a.returnedQuantity}</TableCell>
                  <TableCell>{new Date(a.dueDate!).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right font-bold text-destructive">
                    {daysOverdue(a.dueDate!)} days
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={total}
            pageSize={pageSize}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default OverduePage;
